export type AttendanceStatus =
	| "kelmagan"
	| "kelgan"
	| "kechikkan"
	| "erta_ketgan"
	| "davom_etmoqda"
	| "dam"
	// ── 3-bosqich ──
	| "uzrli";

/**
 * Status → yorliq va rang.
 *
 * ⚠️ Bu yerda, sahifada EMAS — `AttendanceDaysPage` dan tashqari xodim
 * profilining «Davomat» yorlig'i ham shu lug'atni o'qiydi va ikki nusxa
 * jimgina ajralib ketardi.
 *
 * ⚠️ `Record<AttendanceStatus, …>` — typecheck HAR yozuvni MAJBURLAYDI.
 * Usiz `uzrli` kun jadvalda yorliqsiz (`undefined`) chiqardi va filtr
 * ro'yxatida ham ko'rinmasdi.
 *
 * ⚠️ **P7 i18n: bu obyekt ATAYLAB tarjima qilinmaydi.** Bu modul
 * TASHQARISIDA (`employees/components/RecentDaysTable.tsx`,
 * `employees/components/employeeTabs.tsx`) plain obyekt sifatida import
 * qilinadi — ikkalasi ham `modules/employees` chegarasida va bu vazifa
 * doirasidan TASHQARIDA, ya'ni uni hookka yoki tarjima funksiyasiga
 * bog'lab bo'lmaydi. `modules/absences`dagi bir xil naqsh
 * (`ABSENCE_STATUS_LABELS` — o'zgarmagan qoladi, alohida
 * `ABSENCE_STATUS_COLORS` + `t()` shu modulning O'Z ichida ishlatiladi):
 * shu modulning O'Z komponentlari pastdagi `ATTENDANCE_STATUS_COLORS`
 * (faqat rang, tilga bog'liq emas) + `useTranslation("attendance")` +
 * `t(\`status.${value}\`)` orqali ko'rsatadi, bu Record esa faqat
 * meros/cross-module iste'molchilar uchun O'ZGARMAY qoladi.
 */
export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, { label: string; color: string }> =
	{
		kelgan: { label: "Kelgan", color: "green" },
		kechikkan: { label: "Kechikkan", color: "orange" },
		// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
		erta_ketgan: { label: "Erta ketgan", color: "gold" },
		kelmagan: { label: "Kelmagan", color: "red" },
		// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
		davom_etmoqda: { label: "Davom etmoqda", color: "blue" },
		dam: { label: "Dam", color: "default" },
		uzrli: { label: "Uzrli", color: "purple" },
	};

/**
 * Faqat rang — tilga bog'liq emas. Shu modulning O'Z komponentlari
 * (`AttendanceDaysPage`) matnni `ATTENDANCE_STATUS_LABELS.label`dan EMAS,
 * `t(\`status.${value}\`)`dan oladi (yuqoridagi izohga qarang).
 */
export const ATTENDANCE_STATUS_COLORS: Record<AttendanceStatus, string> = {
	kelgan: "green",
	kechikkan: "orange",
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	erta_ketgan: "gold",
	kelmagan: "red",
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	davom_etmoqda: "blue",
	dam: "default",
	uzrli: "purple",
};

/** Select opsiyalari tartibi (`AttendanceDaysPage` filtri). */
export const ATTENDANCE_STATUS_VALUES: AttendanceStatus[] = [
	"kelgan",
	"kechikkan",
	"erta_ketgan",
	"kelmagan",
	"davom_etmoqda",
	"dam",
	"uzrli",
];

export interface AttendanceViolation {
	kind: string;
	details: Record<string, unknown>;
}

export interface AttendanceDay {
	id: string;
	employeeId: string;
	shiftInstanceId: string | null;
	workDate: string;
	status: AttendanceStatus;
	firstCheckInAt: string | null;
	lastCheckOutAt: string | null;
	lateMinutes: number;
	earlyMinutes: number;
	timeInZoneMin: number;
	timeOutsideMin: number;
	departuresCount: number;
	violations: AttendanceViolation[];
	computedAt: string;
	/**
	 * Proyeksiya qaysi watermark ustida qurilgan — TAFSILOT uchun ko'rsatiladi.
	 *
	 * ⚠️ Kun DASTLABKImi degan savolga bu maydon YOLG'IZ javob BERMAYDI:
	 * mezon domenda va u `violations` dagi `toliqsiz_malumot` orqali keladi
	 * (`utils/preliminary.ts`).
	 */
	computedFromWatermark: string | null;
}

export interface AttendanceEvent {
	id: string;
	type: "check_in" | "check_out" | "departure" | "return" | "correction" | "rejection";
	occurredAt: string;
	recordedAt: string;
	source: "mobile" | "derived" | "manual";
	zoneId: string | null;
	photoFileId: string | null;
	reason: string | null;
	correctedEventId: string | null;
	createdBy: string | null;
	payload: Record<string, unknown> | null;
}

export interface AttendanceDayDetail {
	day: AttendanceDay;
	events: AttendanceEvent[];
}

export interface CorrectionInput {
	type: "check_in" | "check_out" | "departure" | "return" | "correction";
	occurredAt: string;
	reason: string;
	correctedEventId?: string;
	zoneId?: string;
}

// ── Jonli xarita (Q3) ──

export type LiveMapMode = "shift" | "active";

export interface LiveMapItem {
	employeeId: string;
	fullName: string;
	positionTitle: string | null;
	departmentName: string | null;
	lat: number;
	lon: number;
	occurredAt: string;
	/** Nuqta yangilik oynasidan eski — marker so'lg'in chiziladi. */
	stale: boolean;
	accuracyM: number | null;
	batteryLevel: number | null;
	gpsEnabled: boolean | null;
	internetStatus: string | null;
	zoneId: string | null;
	zoneName: string | null;
	/** Nuqta tushgan hudud arxivlanganmi; `null` — nuqta hech qaysi hududda emas. */
	zoneArchived: boolean | null;
	insideAssignedZone: boolean | null;
	suspicionFlags: string[];
}

export type LiveMapMissingSabab = "qurilma_yoq" | "paket_yoq";

/** Smenada bo'lishi kerak, lekin oxirgi 24 soatda nuqtasi yo'q xodim (faqat `shift`). */
export interface LiveMapMissing {
	employeeId: string;
	fullName: string;
	departmentName: string | null;
	sabab: LiveMapMissingSabab;
	oxirgiPaketAt: string | null;
}

export interface LiveMapPayload {
	mode: LiveMapMode;
	items: LiveMapItem[];
	missing: LiveMapMissing[];
	/** `items` xarita chegarasida kesilgan. */
	truncated: boolean;
}

// ── Harakat yo'li (P8 §8) ──

export interface EmployeeRoutePoint {
	lat: number;
	lon: number;
	occurredAt: string;
}
